import { useState } from 'react';
import { supabase } from '../config/supabaseClient';
import './RatingModal.css';

/* ─────────────────────────────────────────────────────────────
   RATING MODAL — 2-step placement survey for new users.

   Step 1: pick an experience tier → sets starting ELO directly
           (tiers 1–4) or advances to step 2 (has tournament rating).
   Step 2: enter an official tournament rating → compressed to
           prevent inflated starting ELOs.

   On confirm: updates profiles.elo_rating + profiles.is_rated,
   then calls onRated() so the dashboard refreshes.
   ───────────────────────────────────────────────────────────── */

// Compress a self-reported tournament rating to prevent inflation.
// Anything at or below 1200 is taken at face value.
// Anything above 1200 gets halfway compression toward 1200.
// Examples: 1600 → 1400, 2000 → 1600, 3000 → 2100
function compressRating(input) {
  const PIVOT = 1200;
  if (input <= PIVOT) return input;
  return Math.round(PIVOT + (input - PIVOT) * 0.5);
}

const TIERS = [
  { label: "I'm new to table tennis",                         elo: 500  },
  { label: "I play casually",                                 elo: 700  },
  { label: "I play at a club",                                elo: 1000 },
  { label: "I've played in tournaments (no official rating)", elo: 1200 },
  { label: "I have an official tournament rating",            elo: null }, // → step 2
];

function RatingModal({ isOpen, onClose, userId, onRated }) {
  const [step,         setStep]         = useState(1);
  const [ratingInput,  setRatingInput]  = useState('');
  const [inputError,   setInputError]   = useState('');
  const [saving,       setSaving]       = useState(false);

  if (!isOpen) return null;

  // ── Helpers ────────────────────────────────────────────────

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) handleClose();
  }

  function handleClose() {
    setStep(1);
    setRatingInput('');
    setInputError('');
    onClose();
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') handleClose();
  }

  // Called when the user picks any tier on step 1
  async function handleTierSelect(tier) {
    if (tier.elo === null) {
      // Has an official rating — go to step 2
      setStep(2);
      return;
    }
    // Tiers 1–4: save immediately
    await saveRating(tier.elo);
  }

  // Called from step 2 when user confirms their tournament rating
  async function handleTournamentConfirm() {
    const raw = parseInt(ratingInput, 10);
    if (isNaN(raw) || raw < 0 || raw > 3000) {
      setInputError('Please enter a rating between 0 and 3000.');
      return;
    }
    await saveRating(compressRating(raw));
  }

  async function saveRating(elo) {
    setSaving(true);
    // Use the SECURITY DEFINER RPC instead of a direct profile UPDATE.
    // Direct writes to elo_rating / doubles_elo / is_rated are blocked by
    // column-level privileges (BUG-041 fix). The RPC validates the range
    // and enforces one-shot semantics server-side.
    const { error } = await supabase.rpc('set_initial_rating', { p_rating: elo });
    setSaving(false);
    if (error) {
      setInputError('Could not save rating. Please try again.');
      return;
    }
    // Reset local modal state, but do NOT call handleClose() here —
    // handleClose() calls onClose which nullifies pendingMatchNotif
    // before onRated can process the pending match confirmation.
    setStep(1);
    setRatingInput('');
    setInputError('');
    onRated();
  }

  return (
    <div
      className="modal-backdrop"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
    >
      <div className="modal-card rm-card">

        {/* Header */}
        <div className="modal-header">
          <span className="modal-title">
            {step === 1 ? 'Get Your Estimated Rating' : 'Enter Your Tournament Rating'}
          </span>
          <button className="modal-close" onClick={handleClose} aria-label="Close">
            &times;
          </button>
        </div>

        {/* Step 1 — experience picker */}
        {step === 1 && (
          <div className="rm-step1">
            <p className="rm-intro">
              We'll set a starting rating based on your experience so you're
              matched with the right players from day one.
            </p>
            <div className="rm-tiers">
              {TIERS.map((tier, i) => (
                <button
                  key={i}
                  className="rm-tier-btn"
                  onClick={() => handleTierSelect(tier)}
                  disabled={saving}
                >
                  <span className="rm-tier-label">{tier.label}</span>
                  {tier.elo !== null && (
                    <span className="rm-tier-elo">{tier.elo}</span>
                  )}
                  <span className="rm-tier-arrow">→</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 — tournament rating input */}
        {step === 2 && (
          <div className="rm-step2">
            <p className="rm-intro">
              Enter your official rating (e.g. USATT, ITTF). We'll use a
              slight compression to keep the leaderboard competitive.
            </p>
            <input
              className="rm-rating-input"
              type="number"
              min="0"
              max="3000"
              placeholder="e.g. 1850"
              value={ratingInput}
              onChange={e => { setRatingInput(e.target.value); setInputError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') handleTournamentConfirm(); }}
              autoFocus
            />
            {inputError && <p className="rm-input-error">{inputError}</p>}
            {ratingInput && !isNaN(parseInt(ratingInput, 10)) && parseInt(ratingInput, 10) >= 0 && parseInt(ratingInput, 10) <= 3000 && (
              <p className="rm-preview">
                Starting rating: <strong>{compressRating(parseInt(ratingInput, 10))}</strong>
              </p>
            )}
            <div className="rm-step2-actions">
              <button
                className="rm-back-btn"
                onClick={() => { setStep(1); setRatingInput(''); setInputError(''); }}
                disabled={saving}
              >
                Back
              </button>
              <button
                className="rm-confirm-btn"
                onClick={handleTournamentConfirm}
                disabled={saving || !ratingInput.trim()}
              >
                {saving ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default RatingModal;
